import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Box Spring Cover — per Quoting_markdown/box_spring_covers.md: fabric and
// lining are fixed by bed size (see BOX_SPRING_TABLE, sopCalculations.ts),
// so pricing only needs Bed Size. Box Spring Length/Width/Height are also
// collected here (reusing the generic widthInches/heightInches/depthInches
// columns, same convention as tablecloths) — not used by calcBoxSpringCover
// itself, but so they flow through to the Work Order's "Box Spring L/W/H"
// traveler field pre-filled instead of left blank for the installer to
// measure and write in by hand.
//
// Unlike most item types, this form's values are submitted through dedicated
// addBoxSpringCoverDraftQuoteObject/updateBoxSpringCoverDraftQuoteObject
// mutations (see DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject)
// rather than the generic addDraftQuoteObject/updateDraftQuoteObject every
// non-dedicated-resolver type shares. getValues()'s return shape still needs
// to match what buildInitialValuesFromObject (DraftQuoteDetail.tsx) exposes,
// since "go back and edit" round-trips through those same field names
// regardless of which mutation saved them.
const BoxSpringCoverForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [bedSize, setBedSize] = useState<'twin' | 'full' | 'queen' | 'king' | ''>((iv?.bedSize as any) || '');
  const [length, setLength] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [width, setWidth] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [height, setHeight] = useState(iv?.depthInches != null ? String(iv.depthInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!bedSize) {
        setError('Bed size is required');
        return null;
      }
      setError(null);
      return {
        bedSize,
        widthInches: length ? parseFloat(length) : null,
        heightInches: width ? parseFloat(width) : null,
        depthInches: height ? parseFloat(height) : null,
        fabricDescription: fabricDescription.trim() || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Bed Size</label>
          <select
            className="input"
            value={bedSize}
            onChange={(e) => setBedSize(e.target.value as 'twin' | 'full' | 'queen' | 'king' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="twin">Twin</option>
            <option value="full">Full</option>
            <option value="queen">Queen</option>
            <option value="king">King</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Box Spring Length (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Box Spring Width (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Box Spring Height (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default BoxSpringCoverForm;
